<template>
  <div
    class="device-status"
    ref="statusPage"
    v-loading="loading"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <div class="pull-tip" :class="{ visible: pullDistance }" :style="{ height: pullDistance + 'px' }">
      {{ pulling ? '松开刷新' : '下拉刷新' }}
    </div>

    <div v-if="!farmId" class="empty">缺少农场 ID</div>
    <template v-else>
      <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
      <template v-if="hasVisibleCards">
        <section class="section">
          <div class="cards">
            <article v-for="item in valveCards" :key="item.title" class="status-card">
              <h3>{{ item.title }}</h3>
            <div class="metrics"><div v-for="metric in visibleMetrics(item.items)" :key="metric.label" class="metric"><strong :class="metricClass(metric)">{{ metric.value }}</strong><span>{{ metric.label }}</span></div></div>
            </article>
          </div>
        </section>
        <section class="section">
          <div class="cards">
            <article v-for="item in visibleDeviceStats" :key="item.title" class="status-card">
              <h3>{{ item.title }}</h3>
            <div class="metrics"><div v-for="metric in visibleMetrics(item.items)" :key="metric.label" class="metric"><strong :class="metricClass(metric)">{{ metric.value }}</strong><span>{{ metric.label }}</span></div></div>
            </article>
          </div>
        </section>
      </template>
      <div v-else-if="!loading" class="empty">暂无设备</div>
    </template>
  </div>
</template>

<script>
import {
  getFarmById, getGWs, getSoilMoistureLists, getWeatherStationLists,
  getSporeDevices, getInsecticidalLamps, getMonitors, getPestMonitorPictures,
} from '@/utils/getxx'

export default {
  data() {
    return { loading: false, errorMsg: '', farm: {}, gatewayList: [], soilList: [], weatherList: [], counts: {}, touchStartY: 0, pullDistance: 0, canPull: false }
  },
  computed: {
    farmId() { return this.$route.query.farmId },
    valveStats() { return this.valveItems(this.farm.total2, this.farm.opening2, this.farm.lowVolt2, this.farm.unnormal2) },
    butterflyStats() { return this.valveItems(this.farm.totaldf, this.farm.openingdf, this.farm.lowVoltdf, this.farm.unnormaldf) },
    pulling() { return this.pullDistance >= 56 },
    valveCards() { return [{ title: '阀门', items: this.valveStats }, { title: '蝶阀', items: this.butterflyStats }].filter(item => item.items[0].value > 0) },
    deviceStats() {
      const gatewayOnline = this.gatewayList.filter(item => Number(item.status) === 1).length
      const soilOnline = this.soilList.filter(item => this.isOnline(item.collectTime || item.deviceTime, 24 * 60 + 10)).length
      const weatherOnline = this.weatherList.filter(item => this.isOnline(item.deviceTime || item.collectTime, 65)).length
      return [
        { title: '网关', items: this.onlineItems(this.gatewayList.length, gatewayOnline) },
        { title: '墒情', items: this.onlineItems(this.soilList.length, soilOnline) },
        { title: '气象', items: this.onlineItems(this.weatherList.length, weatherOnline) },
        { title: '孢子', items: this.totalItems(this.counts.spore) },
        { title: '杀虫灯', items: this.totalItems(this.counts.lamp) },
        { title: '监控', items: this.totalItems(this.counts.monitor) },
        { title: '虫情', items: this.totalItems(this.counts.pest) },
      ]
    },
    visibleDeviceStats() { return this.deviceStats.filter(item => item.items[0].value > 0) },
    hasVisibleCards() { return this.valveCards.length + this.visibleDeviceStats.length > 0 },
  },
  mounted() { this.load() },
  methods: {
    list(response) { return Array.isArray(response) ? response : (response && Array.isArray(response.data) ? response.data : []) },
    number(value) { return Number(value) || 0 },
    valveItems(total, open, low, offline) { return [{ label: '总数', value: this.number(total) }, { label: '开启数', value: this.number(open) }, { label: '低电数', value: this.number(low) }, { label: '离线数', value: this.number(offline) }] },
    onlineItems(total, online) { return [{ label: '总数', value: total }, { label: '在线数', value: online }, { label: '离线数', value: total - online }] },
    totalItems(total) { return [{ label: '总数', value: total || 0 }] },
    visibleMetrics(items) { return items.filter(metric => !['开启数', '低电数', '离线数'].includes(metric.label) || metric.value > 0) },
    metricClass(metric) {
      if (!metric.value) return ''
      if (metric.label === '开启数' || metric.label === '在线数') return 'green'
      if (metric.label === '低电数') return 'yellow'
      if (metric.label === '离线数') return 'red'
      return ''
    },
    onTouchStart(event) {
      this.canPull = event.currentTarget.scrollTop === 0
      this.touchStartY = event.touches[0].clientY
    },
    onTouchMove(event) {
      if (!this.canPull) return
      const distance = event.touches[0].clientY - this.touchStartY
      this.pullDistance = distance > 0 ? Math.min(distance / 2, 70) : 0
    },
    onTouchEnd() {
      const shouldRefresh = this.pulling
      this.pullDistance = 0
      this.canPull = false
      if (shouldRefresh && !this.loading) this.load()
    },
    isOnline(value, minutes) {
      const time = new Date(String(value || '').replace(/-/g, '/')).getTime()
      return Number.isFinite(time) && Date.now() - time <= minutes * 60 * 1000
    },
    async load() {
      if (!this.farmId) return
      this.loading = true
      this.errorMsg = ''
      try {
        const result = await Promise.allSettled([
          getFarmById({ farmId: this.farmId }), getGWs({ farmId: this.farmId }),
          getSoilMoistureLists({ farmId: this.farmId }), getWeatherStationLists({ farmId: this.farmId }),
          getSporeDevices({ farmId: this.farmId }), getInsecticidalLamps({ farmId: this.farmId }),
          getMonitors({ farmId: this.farmId }), getPestMonitorPictures({ farmId: this.farmId }),
        ])
        const value = index => result[index].status === 'fulfilled' ? result[index].value : null
        const names = ['阀门', '网关', '墒情', '气象', '孢子', '杀虫灯', '监控', '虫情']
        const failed = result.map((item, index) => item.status === 'rejected' ? names[index] : null).filter(Boolean)
        this.farm = value(0) || {}
        this.gatewayList = this.list(value(1)); this.soilList = this.list(value(2)); this.weatherList = this.list(value(3))
        this.counts = { spore: this.list(value(4)).length, lamp: this.list(value(5)).length, monitor: this.list(value(6)).length, pest: this.list(value(7)).length }
        if (failed.length) this.errorMsg = `部分统计获取失败：${failed.join('、')}`
      } catch (error) {
        this.errorMsg = '设备状态获取失败，请稍后重试'
      } finally { this.loading = false }
    },
  },
}
</script>

<style scoped>
.device-status { box-sizing: border-box; height: 100vh; overflow-y: auto; padding: 0 0 calc(24px + env(safe-area-inset-bottom, 0px)); -webkit-overflow-scrolling: touch; overscroll-behavior-y: contain; background: #edf6ff; color: #26374a; text-align: left; touch-action: pan-y; }
.pull-tip { display: flex; align-items: center; justify-content: center; overflow: hidden; color: #7f92a7; font-size: 12px; transition: height .15s; }.pull-tip:not(.visible) { height: 0 !important; }
.section { padding: 10px 10px 0; }.section + .section { padding-top: 10px; }.cards { display: grid; grid-template-columns: 1fr; gap: 10px; }
.status-card { margin: 0; border-radius: 10px; border-left: 4px solid #5ba1e6; background: #fff; overflow: hidden; box-shadow: 0 3px 10px rgba(62, 123, 183, .1); }.status-card h3 { margin: 0; padding: 9px 12px; font-size: 15px; color: #315372; border-bottom: 1px solid #e4f0fb; }.metrics { display: flex; min-height: 60px; align-items: center; }.metric { flex: 1; min-width: 0; text-align: center; border-right: 1px solid #e4f0fb; }.metric:last-child { border-right: 0; }.metric strong { display: block; color: #1687d4; font-size: 19px; line-height: 25px; }.metric strong.green { color: #28a95a; }.metric strong.yellow { color: #e4a51b; }.metric strong.red { color: #e64b52; }.metric span { display: block; color: #8096ad; font-size: 11px; }.error, .empty { margin: 16px; text-align: center; color: #e56a6a; }.empty { color: #8392a5; }
</style>
